import React, { memo } from "react";
import {
  AuthorizationInfo,
  AuthorizationStatus,
  AuthorizationStatusMap,
  ClientReportReportStatus,
  ClientReportReportStatusMap,
  getConcideredPendingAuthorization,
  PendingAuthorizationInfo,
  shouldCheckPendingAuthorizationByReportType
} from "../domain";
import { SequentialDialog, DialogConfig, DialogStepData } from "common/component/SequentialDialog";
import { BehaviorSubject } from "rxjs";
import {
  SfReportType,
  DialogActionData,
  DialogConditions,
  ClientReportDialogSubmitParams,
  createInactiveAuthDialogConfig,
  createPendingAuthDialogConfig,
  createReportAcceptedDialogConfig,
  createReportInReviewDialogConfig,
  createReportReplacementDialogConfig,
  createApprovedAddendumConflictDialogConfig,
  findApprovedAddendumConflict
} from "./dialogs";

export type { ClientReportDialogSubmitParams, SfReportType }

export type ClientReportDialogManagerProps = {
  auth?: AuthorizationInfo;
  show$: BehaviorSubject<boolean>;
  selectedReportType?: string;
  inReviewReportTypes: SfReportType[];
  authorizedReportTypes: SfReportType[];
  cancelCallback: () => void;
  submitCallback: (params: ClientReportDialogSubmitParams) => void;
}

const evaluateDialogConditions = (
  auth?: AuthorizationInfo,
  selectedReportType?: string,
  inReviewReportTypes: SfReportType[] = [],
  authorizedReportTypes: SfReportType[] = []
): DialogConditions => {
  const isAuthInactive = AuthorizationStatusMap.get(auth?.status ?? "") === AuthorizationStatus.Inactive;
  const isPendingAuthCheck = getConcideredPendingAuthorization(auth).length > 0 && shouldCheckPendingAuthorizationByReportType(selectedReportType);
  
  const shouldShowReplacementWarning = evaluateReplacementDialogConditions(auth, selectedReportType);
  const shouldShowApprovedAddendumConflict = !!findApprovedAddendumConflict(auth, selectedReportType ?? "");

  // Only show old dialogs if replacement warning is not shown
  const isReportAccepted = !shouldShowReplacementWarning && auth?.latestSubmittedClientReport && 
    authorizedReportTypes.map(item => item.reportType).includes(selectedReportType ?? "");
  const isAnotherReportInReview = !shouldShowReplacementWarning && inReviewReportTypes && 
    inReviewReportTypes.length > 0 && 
    !inReviewReportTypes.map(item => item.reportType).includes(selectedReportType ?? "");

  return {
    shouldShowAuthInactiveWarning: !!isAuthInactive,
    shouldShowPendingAuthWarning: !!isPendingAuthCheck,
    shouldShowReportAcceptedConfirmation: !!isReportAccepted,
    shouldShowReportInReviewConfirmation: !!isAnotherReportInReview,
    shouldShowReportReplacementWarning: shouldShowReplacementWarning && !shouldShowApprovedAddendumConflict,
    shouldShowApprovedAddendumConflict
  };
};

const evaluateReplacementDialogConditions = (
  auth?: AuthorizationInfo,
  selectedReportType?: string,
): boolean => {
  const existingReport = auth?.latestSubmittedClientReportByReportType?.find(
    item => item.reportType === selectedReportType
  );
  
  if (!existingReport || !selectedReportType) return false;
  
  const reportStatus = ClientReportReportStatusMap.get(existingReport.reportStatus);

    return [
        ClientReportReportStatus.REVIEW_IN_PROGRESS,
        ClientReportReportStatus.RETURNED_TO_PROVIDER_FOR_CORRECTIONS,
        ClientReportReportStatus.PENDING_REVIEW_OF_PROVIDER_REVISIONS,
        ClientReportReportStatus.FUNDER_REVISION_REQUESTED,
        ClientReportReportStatus.FUNDER_REVISION_REQUESTED_ADMIN_REVIEW,
        ClientReportReportStatus.REPORT_AUTHORIZED,
        ClientReportReportStatus.SUBMITTED_TO_FUNDER,
    ].includes(reportStatus as ClientReportReportStatus);
};

// Main component

export const ClientReportDialogManager = memo(({
  auth,
  show$,
  selectedReportType,
  inReviewReportTypes,
  authorizedReportTypes,
  cancelCallback,
  submitCallback,
}: ClientReportDialogManagerProps) => {

  const conditions = evaluateDialogConditions(auth, selectedReportType, inReviewReportTypes, authorizedReportTypes);
  const pendingAuths = getConcideredPendingAuthorization(auth);

  // Dialog configurations
  const dialogConfigs: DialogConfig[] = [
    createInactiveAuthDialogConfig(conditions.shouldShowAuthInactiveWarning),
    createPendingAuthDialogConfig(pendingAuths, conditions.shouldShowPendingAuthWarning),
    createApprovedAddendumConflictDialogConfig(auth, selectedReportType ?? '', conditions.shouldShowApprovedAddendumConflict),
    createReportReplacementDialogConfig(auth, selectedReportType ?? '', conditions.shouldShowReportReplacementWarning),
    createReportAcceptedDialogConfig(selectedReportType ?? '', authorizedReportTypes, conditions.shouldShowReportAcceptedConfirmation),
    createReportInReviewDialogConfig(inReviewReportTypes, conditions.shouldShowReportInReviewConfirmation)
  ];

  const handleCompletion = (collectedData: DialogStepData[]) => {
    if (collectedData.length === 0) return;
    
    // Collect data from ALL steps, not just the final step
    let pendingAuthToUpdate: PendingAuthorizationInfo | undefined;
    let reviseReportType: string | undefined;
    let shouldForceCreateNewOperationCase = false;
    let shouldUpdateActiveAuth = false;
    
    // Look through all collected step data to find the relevant information
    for (const stepData of collectedData) {
      const actionData: DialogActionData = stepData.buttonData;
      if (!actionData) continue;

      // if any steps contain action cancel, close the modal without submitting 
      if (actionData.action === 'cancel') {
        cancelCallback();
        return;
      }
      
      // Capture pending auth data from any step that has it
      if (actionData.pendingAuthToUpdate) {
        pendingAuthToUpdate = actionData.pendingAuthToUpdate;
      }
      
      // Capture other action data
      if (actionData.reviseReportType) {
        reviseReportType = actionData.reviseReportType;
      }
      
      if (actionData.action === 'forceSubmitNew') {
        shouldForceCreateNewOperationCase = true;
      }
      
      if (actionData.shouldUpdateActiveAuth) {
        shouldUpdateActiveAuth = true;
      }
    }
    
    const payload: ClientReportDialogSubmitParams = {
      pendingAuthToUpdate,
      reviseReportType,
      shouldForceCreateNewOperationCase: shouldForceCreateNewOperationCase || undefined,
      shouldUpdateActiveAuth,
    };

    submitCallback(payload);
  };

  return (
    <SequentialDialog
      show$={show$}
      dialogs={dialogConfigs}
      onCancel={cancelCallback}
      onComplete={handleCompletion}
    />
  );
});

export const shouldShowClientReportDialog = (
  auth?: AuthorizationInfo,
  inReviewReportTypes: SfReportType[] = [],
  authorizedReportTypes: SfReportType[] = [],
  selectedReportType?: string
): boolean => {
  const conditions = evaluateDialogConditions(auth, selectedReportType, inReviewReportTypes, authorizedReportTypes);
  
  return Object.values(conditions).some(condition => condition);
};
